'use client';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  IConversation,
  IConversationCohortFollowPreview,
  Spinner,
  Switch,
  isCohortChatConversation,
  useConversationContext,
} from '@ui/components';

export const COHORT_FOLLOW_LABEL = 'Add people who join this cohort later';
export const COHORT_FOLLOW_HELP =
  'Off: only the people in the cohort right now. On: new cohort members join with the history; people who leave the cohort can still read but not write.';
export const COHORT_FOLLOW_OFF_MESSAGE =
  'No one else will be added. Current members and read-only former members stay as they are.';
export const COHORT_MISSING_MESSAGE = 'This cohort no longer exists.';
const PREVIEW_FAILED_MESSAGE = "Couldn't check the cohort. Try again.";
const SAVE_FAILED_MESSAGE = "Couldn't save this setting. Try again.";

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The ON confirm text: what the switch will do, as of the cohort's last rebuild. */
export const describeCohortFollowPreview = (preview: IConversationCohortFollowPreview) =>
  `${count(
    preview.will_add ?? 0,
    'person',
    'people',
  )} who joined the cohort later will be added and notified. ` +
  `${count(
    preview.will_turn_read_only ?? 0,
    'current member is',
    'current members are',
  )} no longer in the cohort and will become read-only (they keep the history).`;

/** `cohort_missing` is the one refusal with its own copy; anything else gets the fallback. */
const messageFor = (error: unknown, fallback: string) => {
  const refusal = error as { status?: number | string; data?: { code?: string } } | undefined;
  return refusal?.status === 422 && refusal?.data?.code === 'cohort_missing'
    ? COHORT_MISSING_MESSAGE
    : fallback;
};

interface IProps {
  conversation: IConversation;
}

/**
 * The creator's per-chat choice between fixed members and following the cohort. Each
 * switch confirms first: ON shows what the sync will do, OFF says nothing else changes.
 * Saves on confirm through its own endpoint, not with the settings Save button.
 */
export const ConversationCohortFollow = ({ conversation }: IProps) => {
  const state = useConversationContext();
  const [followsCohort, setFollowsCohort] = useState(Boolean(conversation.follows_cohort));
  const [updatedAt, setUpdatedAt] = useState<string | null>(
    conversation.follows_cohort_updated_at ?? null,
  );
  const [pending, setPending] = useState<'on' | 'off' | null>(null);
  const [preview, setPreview] = useState<IConversationCohortFollowPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFollowsCohort(Boolean(conversation.follows_cohort));
    setUpdatedAt(conversation.follows_cohort_updated_at ?? null);
  }, [conversation.id, conversation.follows_cohort, conversation.follows_cohort_updated_at]);

  if (!isCohortChatConversation(conversation)) return null;

  const askToSwitch = async (checked: boolean) => {
    setError(null);
    setPreview(null);
    if (!checked) {
      setPending('off');
      return;
    }
    setPending('on');
    setIsLoadingPreview(true);
    try {
      const result = await state.getConversationCohortFollow(conversation.id);
      if (result.preview.cohort_missing) {
        // The cohort row is gone: nothing to follow, so no switch on.
        setError(COHORT_MISSING_MESSAGE);
      } else {
        setPreview(result.preview);
      }
    } catch (reason) {
      setError(messageFor(reason, PREVIEW_FAILED_MESSAGE));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const close = () => {
    if (isSaving) return;
    setPending(null);
    setPreview(null);
    setError(null);
  };

  const confirm = async () => {
    if (!pending) return;
    setIsSaving(true);
    setError(null);
    try {
      const result = await state.updateConversationCohortFollow(conversation.id, pending === 'on');
      setFollowsCohort(result.follows_cohort);
      setUpdatedAt(result.follows_cohort_updated_at);
      setPending(null);
      setPreview(null);
    } catch (reason) {
      setError(messageFor(reason, SAVE_FAILED_MESSAGE));
    } finally {
      setIsSaving(false);
    }
  };

  const turningOn = pending === 'on';
  const cannotConfirm =
    isSaving || isLoadingPreview || (turningOn && !preview) || error === COHORT_MISSING_MESSAGE;

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium">Members</p>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-slate-500" htmlFor="conversation-follows-cohort">
            {COHORT_FOLLOW_LABEL}
          </label>
          <span className="text-xs leading-tight text-gray-500">{COHORT_FOLLOW_HELP}</span>
          {updatedAt && (
            <span className="text-xs text-gray-400">
              Changed {dayjs(updatedAt).format('MMM D, YYYY')}
            </span>
          )}
        </div>
        <Switch
          id="conversation-follows-cohort"
          aria-label={COHORT_FOLLOW_LABEL}
          checked={followsCohort}
          disabled={pending !== null}
          onCheckedChange={askToSwitch}
        />
      </div>

      <AlertDialog open={pending !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {turningOn
                ? 'Add people who join this cohort later?'
                : 'Stop adding people who join later?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {turningOn ? (
                isLoadingPreview ? (
                  <span className="flex items-center gap-2">
                    <Spinner className="h-4 w-4 animate-spin" fill="#2D3139" /> Checking the cohort…
                  </span>
                ) : (
                  preview && <span>{describeCohortFollowPreview(preview)}</span>
                )
              ) : (
                <span>{COHORT_FOLLOW_OFF_MESSAGE}</span>
              )}
              {error && (
                <span role="alert" className="block text-red-600">
                  {error}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={close} disabled={isSaving}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary hover:bg-primaryHover"
              disabled={cannotConfirm}
              onClick={(event) => {
                // Stays open while saving and when the server refuses.
                event.preventDefault();
                void confirm();
              }}>
              {isSaving && <Spinner className="mr-2 animate-spin" fill="#2D3139" />}
              {turningOn ? 'Turn on' : 'Turn off'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
